import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Loader2 } from "lucide-react";

// Debounced autocomplete: curated dividend universe first (instant), yfinance fallback.
// - value/onChange controls the raw text.
// - onSelect fires with { ticker, name } when the user picks a suggestion (click or Enter).
export default function TickerAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Add ticker (e.g. SCHD, JEPI)",
  disabled = false,
  autoFocus = false,
  testId = "ticker-autocomplete",
}) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [source, setSource] = useState("curated");
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce search.
  useEffect(() => {
    const q = (value || "").trim();
    if (!q) { setResults([]); setSource("curated"); return; }
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/tickers/search`, { params: { q, limit: 10 } });
        if (!alive) return;
        setResults(res.data.results || []);
        setSource(res.data.source || "curated");
        setHighlight(0);
      } catch {
        if (!alive) return;
        setResults([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 180);
    return () => { alive = false; clearTimeout(t); };
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (row) => {
    onChange?.(row.ticker);
    onSelect?.(row);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    // Enter always submits — use the highlighted suggestion if there is
    // one, otherwise submit the raw typed value. Without the raw-value
    // path, users who type a ticker faster than the 180ms debounce (or
    // type one that isn't in our curated list) press Enter and get
    // nothing back, which reads as "Research is broken pre-login"
    // (2026-02-14 bug report).
    if (e.key === "Enter") {
      const chosen = results[highlight];
      if (chosen) { e.preventDefault(); pick(chosen); return; }
      const raw = (value || "").trim();
      if (raw) { e.preventDefault(); pick({ ticker: raw.toUpperCase(), name: "" }); return; }
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % results.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + results.length) % results.length); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const showList = open && (loading || results.length > 0 || (value || "").trim());

  return (
    <div ref={boxRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={value || ""}
          onChange={(e) => { onChange?.(e.target.value.toUpperCase()); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          className="pl-9 uppercase mono text-sm tracking-wider"
          data-testid={testId}
          aria-autocomplete="list"
          aria-expanded={showList}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {showList && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 rounded-md border border-border bg-background shadow-lg overflow-hidden"
          data-testid={`${testId}-list`}
        >
          {results.length === 0 && !loading && (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">
              No match. Press <span className="mono">Enter</span> or click <span className="mono">Add</span> to try {`"${(value || "").toUpperCase()}"`} anyway.
            </div>
          )}
          {results.map((r, i) => (
            <button
              type="button"
              key={r.ticker}
              onClick={() => pick(r)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 transition-colors ${
                i === highlight ? "bg-primary/5" : "hover:bg-muted/60"
              }`}
              data-testid={`${testId}-option-${r.ticker}`}
            >
              <div className="flex items-baseline gap-3 min-w-0">
                <span className="mono text-sm font-semibold tracking-wider">{r.ticker}</span>
                <span className="text-xs text-foreground/70 truncate">{r.name}</span>
              </div>
              <span className="mono text-[9px] uppercase tracking-widest text-secondary shrink-0 hidden sm:inline">
                {r.category || (r.sector) || (r.dividend_yield ? `${(r.dividend_yield * 100).toFixed(2)}% yield` : "")}
              </span>
            </button>
          ))}
          <div className="px-3 py-1.5 border-t border-border mono text-[9px] uppercase tracking-widest text-muted-foreground bg-muted/40">
            {source === "curated" ? "Popular dividend universe" : "Yahoo Finance search"}
          </div>
        </div>
      )}
    </div>
  );
}
